import type { GameModel, ReadOnlyGameModel } from 'yage/game/GameModel';
import { Transform } from 'yage/schemas/entity/Transform';
import { AirportZone } from '../components/AirportZone.js';
import { onPad } from '../domain/config.js';
import type { Point } from '../domain/types.js';
import { ids, zoneAvailable } from '../services/world.js';

/** How close to a work desk (world units) joystick steering magnetically attracts the captain. */
export const STICK_MAGNET_RADIUS = 90;

/**
 * Mobile touch control detection: true on touchscreens, mobile viewports, or mobile browsers.
 */
export function isMobileControls(event?: any, smallViewport?: boolean): boolean {
    if (event?.pointerType === 'touch') return true;
    if (typeof navigator !== 'undefined') {
        if (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)) return true;
        if (navigator.maxTouchPoints > 0) return true;
    }
    if (typeof window !== 'undefined') {
        if (window.matchMedia?.('(pointer: coarse)').matches) return true;
        if (window.innerWidth < 768) return true;
    }
    if (smallViewport) return true;
    return false;
}

/**
 * Find the nearest unlocked, active work desk within maxDistance.
 */
export function nearestAvailableWorkDesk(
    g: GameModel | ReadOnlyGameModel,
    at: Point,
    maxDistance = STICK_MAGNET_RADIUS,
): { zone: number; point: Point; dist: number } | null {
    let best: { zone: number; point: Point; dist: number } | null = null;
    let bestDist = maxDistance;

    for (const id of ids(g, AirportZone)) {
        const z = g.getTypedUnsafe(AirportZone, id);
        if (z.kind !== 'work' || !zoneAvailable(g, id)) continue;
        const p = g.getTypedUnsafe(Transform, id);
        const dist = Math.hypot(p.x - at.x, p.y - at.y);
        if (dist <= bestDist) {
            best = { zone: id, point: { x: p.x, y: p.y }, dist };
            bestDist = dist;
        }
    }
    return best;
}

/**
 * Find an available work desk within attraction radius that the player is steering towards.
 * Once the captain is already standing on the desk pad, returns null so the player can walk away freely.
 */
export function findMagneticWorkDesk(
    g: GameModel | ReadOnlyGameModel,
    at: Point,
    dir: Point,
    maxDistance = STICK_MAGNET_RADIUS,
): { zone: number; point: Point; dist: number } | null {
    let best: { zone: number; point: Point; dist: number } | null = null;
    let bestDist = maxDistance;

    for (const id of ids(g, AirportZone)) {
        const z = g.getTypedUnsafe(AirportZone, id);
        if (z.kind !== 'work' || !zoneAvailable(g, id)) continue;
        const p = g.getTypedUnsafe(Transform, id);

        // If the captain is already within the work pad bounds, do not apply magnetic pull
        // so they can freely leave in any direction without sticking.
        if (onPad('work', at, p)) continue;

        const dx = p.x - at.x;
        const dy = p.y - at.y;
        const dist = Math.hypot(dx, dy);
        if (dist > bestDist) continue;

        if (dist > 0) {
            const nx = dx / dist;
            const ny = dy / dist;
            const dot = dir.x * nx + dir.y * ny;
            // The joystick must steer generally towards the desk (cone of ~80 degrees).
            if (dot < 0.15) continue;
        }

        best = { zone: id, point: { x: p.x, y: p.y }, dist };
        bestDist = dist;
    }

    return best;
}
